import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useTheme } from "@/App";
import { Button } from "@/components/ui/button";
import { BookOpen, Sun, Moon, Sparkles } from "lucide-react";
import { KITT } from "@/constants/hedgekit";

export default function Landing() {
  const { user, login, loading } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  // If already logged in, redirect to home
  useEffect(() => {
    if (!loading && user) {
      navigate('/home', { replace: true });
    }
  }, [loading, user, navigate]);

  // Show nothing while checking auth or redirecting
  if (loading || user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background paper-texture relative overflow-hidden">
      {/* Theme toggle */}
      <button
        onClick={toggleTheme}
        className="absolute top-6 right-6 p-3 rounded-full bg-card hover:bg-muted transition-colors z-10"
        aria-label="Toggle theme"
        data-testid="theme-toggle"
      >
        {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
      </button>

      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-32 h-32 bg-primary/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-48 h-48 bg-secondary/30 rounded-full blur-3xl"></div>

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 py-12">
        {/* Kitt Mascot */}
        <div className="mb-6 animate-slide-up" style={{ animationDelay: '0.1s' }}>
          <img 
            src={KITT.waving} 
            alt="Kitt the hedgekit waving hello" 
            className="w-32 h-32 sm:w-40 sm:h-40 object-contain drop-shadow-lg hover:scale-105 transition-transform"
          />
        </div>

        {/* Title */}
        <h1 
          className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black text-center mb-4 animate-slide-up"
          style={{ animationDelay: '0.2s' }}
          data-testid="landing-title"
        >
          Kitt's Book Nook
        </h1>

        {/* Subtitle */}
        <p 
          className="text-lg md:text-xl text-muted-foreground text-center max-w-md mb-12 animate-slide-up font-ui"
          style={{ animationDelay: '0.3s' }}
        >
          Find your perfect reading adventure. Listen, learn, and explore amazing stories.
        </p>

        {/* Features */}
        <div 
          className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12 max-w-2xl animate-slide-up"
          style={{ animationDelay: '0.4s' }}
        >
          <div className="text-center p-4">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-950/50 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <BookOpen className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <p className="text-sm font-medium">Thousands of Books</p>
          </div>
          <div className="text-center p-4">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-950/50 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <Sparkles className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <p className="text-sm font-medium">Read & Listen</p>
          </div>
          <div className="text-center p-4">
            <div className="w-12 h-12 bg-amber-100 dark:bg-amber-950/50 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <Sun className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
            <p className="text-sm font-medium">Your Reading Level</p>
          </div>
        </div>

        {/* Login Button */}
        <Button
          onClick={login}
          size="lg"
          className="h-14 px-10 rounded-full font-heading font-bold text-lg bg-primary text-primary-foreground hover:bg-primary/90 shadow-button active:shadow-none active:translate-y-1 transition-all animate-slide-up"
          style={{ animationDelay: '0.5s' }}
          data-testid="login-button"
        >
          Get Started
        </Button>

        <p 
          className="mt-4 text-sm text-muted-foreground animate-slide-up"
          style={{ animationDelay: '0.6s' }}
        >
          Sign in with Google to save your progress
        </p>

        {/* Affiliate link - pushed up to avoid Emergent badge on mobile */}
        <a
          href="https://get.emergent.sh/xjnotg1y8n0g"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-8 mb-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/20 text-sm font-medium text-purple-600 dark:text-purple-400 hover:from-purple-500/20 hover:to-blue-500/20 transition-all animate-slide-up"
          style={{ animationDelay: '0.7s' }}
        >
          <span>✨</span>
          Build Your Own App with Emergent
        </a>
        
        {/* Affiliate disclosure */}
        <p className="mb-16 sm:mb-4 text-[10px] text-muted-foreground/40 text-center max-w-xs">
          This site contains affiliate links. We may earn a commission at no extra cost to you.
        </p>
      </div>
    </div>
  );
}
